import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { routes } from '@/app/routes';
import { useCurrentUser, useLogout } from '@/features/auth/useAuth';

import styles from './ProfileMenu.module.css';

/**
 * The avatar, and the two things you want from it.
 *
 * There was a dropdown here once, holding seven destinations, and it was replaced
 * by a plain link into the account section — a dropdown is right for two or three
 * items and wrong for seven, and at seven it was a menu pretending the section it
 * belonged to did not exist. That section stays; this is the small version coming
 * back for the one thing a page cannot hold.
 *
 * The owner asked for it directly: *"de logout knop moet onder mijn avatar als
 * drop down"*. Logging out was living in the middle of the profile *form*, below
 * availability and goals, which is a strange place to keep the way out.
 *
 * A **disclosure**, not `role="menu"`. A menu role promises roving arrow-key
 * focus and typeahead, and for two items that is machinery nobody benefits from
 * and one more thing to get wrong. A button that reveals a link and a button is
 * exactly what this is.
 *
 * No failure state: the avatar degrades on its own. Without a user it shows "?"
 * and is named "Jouw account", and both actions behind it still work — logging
 * out does not need to know who you are. An error box in the header would be
 * louder than the problem and would sit on every screen in the app. A genuinely
 * unreachable `/auth/me` is the session guard's business, not this menu's.
 */
export function ProfileMenu() {
    const { data: user } = useCurrentUser();
    const logout = useLogout();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const name = user?.display_name ?? '';
    const initial = (name === '' ? '?' : name).charAt(0).toUpperCase();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                setIsOpen(false);
                // Focus goes back to the control that opened it, or the user is
                // left at the top of the document with no idea where they were.
                buttonRef.current?.focus();
            }
        }

        function onPointerDown(event: PointerEvent): void {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [isOpen]);

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                ref={buttonRef}
                type="button"
                className={styles.avatar}
                aria-expanded={isOpen}
                /*
                 * Named for the person, not for the letter. "M" is not a label,
                 * and the name tells a screen-reader user whose account they are
                 * about to act on — which matters when one of the two actions is
                 * logging out.
                 */
                aria-label={name === '' ? 'Jouw account' : `Jouw account, ${name}`}
                onClick={() => {
                    setIsOpen((open) => !open);
                }}
            >
                <span aria-hidden="true">{initial}</span>
            </button>

            {isOpen && (
                <div className={styles.panel}>
                    <p className={styles.email}>{user?.email}</p>

                    {/*
                        Closed by the click that navigates, rather than by an
                        effect watching the pathname. Setting state synchronously
                        in an effect triggers a cascading render — ESLint says so
                        and is right — and the link is the only thing in here that
                        navigates, so there is nothing to watch for.
                    */}
                    <Link
                        className={styles.item}
                        to={routes.settings}
                        onClick={() => {
                            setIsOpen(false);
                        }}
                    >
                        Jouw profiel
                    </Link>

                    {/*
                        Styled as the link above it, not as a Button.

                        A `fullWidth` ghost Button centres its label, so the two
                        items in this panel sat on different axes — one left, one
                        centred — which reads as two unrelated controls that happen
                        to be adjacent. They are one list of two things.
                    */}
                    <button
                        type="button"
                        className={styles.item}
                        disabled={logout.isPending}
                        onClick={() => {
                            logout.mutate(undefined, {
                                onSettled: () => {
                                    void navigate(routes.login, { replace: true });
                                },
                            });
                        }}
                    >
                        {logout.isPending ? 'Bezig met uitloggen…' : 'Uitloggen'}
                    </button>
                </div>
            )}
        </div>
    );
}
